import { Injectable } from '@angular/core';
import { ToastController, ToastOptions } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from '@panamax/app-state';
import {
  LoadingState,
  ProductStateService,
  getLoadingStateOfProducts,
} from '@usf/ngrx-product';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  map,
  pairwise,
  switchMap,
  tap,
} from 'rxjs';
import { calculateNoQtyBoxWithLineNumberProductHeight } from '../../product-detail/utils/product-card.util';
import { productWithAlternativesSelector } from '../../shared/selectors/product.selectors';
import { SearchProductNumbersViewModel } from '../model/search-product-numbers.model';
import { ProductRow } from '../shared/list-detail-management/model/list-detail-management-view.model';
import { ProductDrawerMessage } from '../../product-detail/models/product-drawer-message';
import { ProductPricingService } from '@usf/ngrx-pricing';
import { Product } from '../../shared/models/product.model';
import { ListAnalyticsService } from '@usf/ngrx-list';

@Injectable({
  providedIn: 'root',
})
export class SearchProductNumbersService {
  public selections: BehaviorSubject<Set<number>> = new BehaviorSubject(
    new Set(),
  );

  public productNumbersToSearchFor: BehaviorSubject<
    number[]
  > = new BehaviorSubject([]);

  constructor(
    private productStateService: ProductStateService,
    private productPricingService: ProductPricingService,
    private store: Store,
    private platformService: PlatformService,
    private translateService: TranslateService,
    private toastController: ToastController,
    private analytics: ListAnalyticsService,
  ) {}

  searchForProducts(productNumbers: number[]) {
    this.updateInitialSelections(productNumbers);
    this.productStateService.loadProducts(productNumbers);
    this.productNumbersToSearchFor.next(productNumbers);
  }

  updateInitialSelections(productNumbers: number[]) {
    const selectionsSet = new Set<number>();
    for (const productNumber of productNumbers) {
      selectionsSet.add(productNumber);
    }
    this.selections.next(selectionsSet);
  }

  updateSelectionForProduct(productNumber: number) {
    const currentSelections = this.selections.value;
    if (currentSelections.has(productNumber)) {
      currentSelections.delete(productNumber);
    } else {
      currentSelections.add(productNumber);
    }
    this.selections.next(currentSelections);
  }

  deselectAllProducts() {
    this.selections.next(new Set());
  }

  selectAllProducts() {
    const allProductNumbers = this.productNumbersToSearchFor.value;
    this.selections.next(new Set(allProductNumbers));
  }

  resetProductNumberSearch() {
    this.productNumbersToSearchFor.next([]);
  }

  selectLoadingStateForProductNumbers() {
    return this.productNumbersToSearchFor
      .asObservable()
      .pipe(
        switchMap((productNumbers: number[]) =>
          this.store.select(getLoadingStateOfProducts(productNumbers)),
        ),
      );
  }

  selectSearchProductNumbersViewModel(): Observable<SearchProductNumbersViewModel> {
    return combineLatest([
      this.store.select(productWithAlternativesSelector),
      this.selections,
      this.productNumbersToSearchFor,
      this.selectLoadingStateForProductNumbers(),
    ]).pipe(
      pairwise(),
      tap(([prev, curr]) => {
        const currentLoadingState = curr[3];
        const previousLoadingState = prev[3];
        if (
          previousLoadingState === LoadingState.loading &&
          currentLoadingState === LoadingState.loaded
        ) {
          const productMap = curr[0];
          const productNumbers = curr[2];
          this.fetchPricingForFoundProducts(productNumbers, productMap);
        }
        if (
          previousLoadingState !== currentLoadingState &&
          currentLoadingState === LoadingState.error
        ) {
          this.showErrorToast();
        }
      }),
      map(([prev, curr]) => {
        const productsMap = curr[0];
        const selections = curr[1];
        const productNumbers = curr[2];
        const loadingState = curr[3];
        let numberOfProductsFound = 0;
        let numberOfProductsNotFound = 0;
        let lineNumbersNotFound = '';
        let products: ProductRow[] = [];
        let itemHeights: number[] = [];
        let numberOfProductsSelected = 0;
        let lineNumber = 1;
        for (const productNumber of productNumbers) {
          const product = productsMap.get(productNumber);
          if (product?.notFound) {
            numberOfProductsNotFound++;
            if (lineNumbersNotFound === '') {
              lineNumbersNotFound += 'Line ' + lineNumber + ',';
            } else {
              lineNumbersNotFound += ' ' + lineNumber + ',';
            }
          } else {
            numberOfProductsFound++;
            if (selections.has(productNumber)) {
              numberOfProductsSelected++;
            }
          }
          const productRow = {
            ...product,
            hasAlternative: false,
            hideQuantityBoxes: true,
            selected:
              selections.has(product?.productNumber) && !product?.notFound,
            sequenceNumber: lineNumber,
            drawerMsg: product?.notFound
              ? ({
                  isWarning: true,
                  message: 'i18n.productDetail.productNotFound',
                  secondaryMessage: '',
                  secondaryMessageDate: '',
                  iconName: 'close-circle-outline',
                } as ProductDrawerMessage)
              : product?.drawerMsg,
          } as ProductRow;
          products.push(productRow);
          const itemHeight = calculateNoQtyBoxWithLineNumberProductHeight(
            this.platformService.platformType,
            productRow,
          );
          itemHeights.push(itemHeight);
          lineNumber++;
        }

        const isLoaded = loadingState === LoadingState.loaded;
        const showNoResultsFound =
          (products?.length > 0 &&
            isLoaded &&
            numberOfProductsFound === 0 &&
            numberOfProductsNotFound > 0) ||
          loadingState === LoadingState.error;

        const showProductsNotFound =
          products?.length > 0 &&
          isLoaded &&
          numberOfProductsFound > 0 &&
          numberOfProductsNotFound > 0;

        return {
          isLoading: loadingState === LoadingState.loading,
          showProductsFound:
            products?.length > 0 && isLoaded && numberOfProductsFound > 0,
          showFooter: numberOfProductsSelected > 0 && isLoaded,
          productsFound: this.buildProductsFoundString(numberOfProductsFound),
          productsNotFound: this.buildProductsNotFoundString(
            numberOfProductsNotFound,
            lineNumbersNotFound,
          ),
          submitBtnText: this.buildSubmitBtnText(numberOfProductsSelected),
          showProductsNotFound,
          isLoaded,
          showNoResultsFound,
          lineNumbersNotFound,
          products,
          numberOfProductsSelected,
          selections,
          itemHeights,

          numberOfProductsFound,
        } as SearchProductNumbersViewModel;
      }),
    );
  }

  async showErrorToast() {
    if (this.toastController.getTop()) {
      this.toastController.dismiss();
    }
    const toast = await this.toastController.create({
      message: this.translateService.instant('i18n.lists.genericError'),
      cssClass: 'red-toast',
      duration: 5000,
      position: 'top',
    } as ToastOptions);
    await toast.present();
  }

  fetchPricingForFoundProducts(
    productNumbers: number[],
    productMap: Map<number, Product>,
  ) {
    const productNumbersToFetchPricingFor = [];
    for (const productNumber of productNumbers) {
      const product = productMap.get(productNumber);
      if (!product?.notFound) {
        productNumbersToFetchPricingFor.push(productNumber);
      }
    }
    this.productPricingService.loadPrices(productNumbersToFetchPricingFor);
  }

  private buildProductsFoundString(numberOfProductsFound: number) {
    let productsFound = '';
    if (numberOfProductsFound === 1) {
      productsFound +=
        numberOfProductsFound +
        ' ' +
        this.translateService.instant(
          'i18n.lists.searchByProductNumbersModal.oneProductFound',
        );
    } else if (numberOfProductsFound > 1) {
      productsFound +=
        numberOfProductsFound +
        ' ' +
        this.translateService.instant(
          'i18n.lists.searchByProductNumbersModal.multipleProductsFound',
        );
    }
    return productsFound;
  }

  private buildProductsNotFoundString(
    numberOfProductsNotFound: number,
    lineNumbersNotFound: string,
  ) {
    let productsNotFound = '';
    if (numberOfProductsNotFound === 1) {
      productsNotFound +=
        numberOfProductsNotFound +
        ' ' +
        this.translateService.instant(
          'i18n.lists.searchByProductNumbersModal.oneProductNotFound',
        );
    } else if (numberOfProductsNotFound > 1) {
      productsNotFound +=
        numberOfProductsNotFound +
        ' ' +
        this.translateService.instant(
          'i18n.lists.searchByProductNumbersModal.multipleProductsNotFound',
        );
    }
    if (productsNotFound) {
      // Remove trailing comma/whitespace
      const trimmedLineNumbersNotFound = lineNumbersNotFound.replace(
        /,\s*$/,
        '',
      );
      productsNotFound += ' (' + trimmedLineNumbersNotFound + ')';
    }
    return productsNotFound;
  }

  private buildSubmitBtnText(numberOfProductsSelected: number) {
    let submitBtnText;
    if (
      this.platformService?.platformType ===
      this.platformService.platformEnum.mobile
    ) {
      submitBtnText =
        numberOfProductsSelected +
        ' ' +
        this.translateService.instant('i18n.common.selected');
    } else {
      submitBtnText = this.translateService.instant('i18n.common.next');
    }
    return submitBtnText;
  }

  trackSearchByProductNumberPageLoad() {
    this.analytics.trackSearchByProductNumberPageLoaded();
  }
}
